import { nonnull } from "@tento-core/assert";
import { CalendarIcon, LonaIcons } from "@tento-ui/ui/icons";
import { CalendarColumnCellContent } from "@tento-ui/components/calendar/calendar-column-cell-content";
import { CalendarColumnLayoutGroup } from "@tento-ui/components/calendar/calendar-column-layout-group";
import {
  CalendarSubcolumn,
  type CalendarSubcolumnSpan,
} from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  type ContentProviders,
  type RenderPlugin,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { Json, type Option, RowId } from "@tento-lona/sheets";
import type { CellValue } from "@tento-lona/sheets";
import type { TimepointCellEntry, RowLocalId } from "@tento-lona/sheets";
import { type SheetRow } from "@tento-lona";
import { SheetViewModel } from "@tento-lona";
import { SchedulingLinkAttributes } from "@tento-lona/sheets";
import { DateTime, NaiveDate, Time, TimeOfDay, Utc } from "@tento-chrono";
import { platformOf } from "@tento-lona/host";
import { SpanColumn } from "@tento-ui/components/span/components/span-column";
import { SpanFlat } from "@tento-ui/components/span/components/span-flat";
import { Span as ContentSpan } from "@tento-ui/components/span/span";
import {
  SchedulingLinkAvailabilityItem,
  SchedulingLinkBaselineAvailabilityItem,
  type AvailabilityOverride,
  type AvailabilityBaselineSlot,
  type AvailabilitySlot,
  type SchedulingLinkItemDelegate,
} from "./source-scheduling-link/scheduling-link-gesture-item.ts";
import { CalendarGesture } from "@tento-ui/components/calendar/calendar-gesture";
import type { GestureInfo } from "@tento-lona/sheets-ui/sheet/gestures/gesture-info.ts";
import { ExistingEventGestures } from "@tento-lona/sheets-ui/sheet/gestures/existing-event-gestures.ts";
import type { RendererGestureHost } from "@tento-lona/sheets-ui/sheet/gestures/renderer-gesture-host.ts";
import type { SheetBinder } from "../../sheet/sheet-index-renderer.ts";

/**
 * The source-row plugin for a scheduling link: its availability slots, drawn
 * and dragged on the calendar.
 */
export class SchedulingLinkSourcePlugin
  implements RenderPlugin.SourceRow<HTMLElement, SheetLegend.DefaultRow>
{
  readonly role = "source";
  readonly id = "scheduling-link";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(CalendarIcon);
  makeRowOptions = null;
  loadData = null;
  legendFactory = null;

  #delegate: Option<SchedulingLinkItemDelegate> = null;
  #schedulingLinkRowIds = new Set<RowLocalId>();

  /** The delegate `setDelegate` installed (read only once one is). */
  get #installedDelegate(): SchedulingLinkItemDelegate {
    return nonnull(this.#delegate, "SchedulingLink: create handler used with no delegate");
  }

  setDelegate(delegate: SchedulingLinkItemDelegate): void {
    this.#delegate = delegate;
  }

  /**
   * Renderer-declared gesture list: availability slots support Move,
   * Resize, ClickToOpenDetails, and ContextMenu, composed from
   * the shared gesture classes and guarded to
   * `SchedulingLinkAvailabilityItem`.
   */
  static registerGestures(
    manager: CalendarGesture.Manager<GestureInfo>,
    host: RendererGestureHost,
  ): void {
    const spanGuard = (item: object): item is SchedulingLinkAvailabilityItem =>
      item instanceof SchedulingLinkAvailabilityItem;
    const menuGuard = (
      item: object,
    ): item is SchedulingLinkAvailabilityItem | SchedulingLinkBaselineAvailabilityItem =>
      item instanceof SchedulingLinkAvailabilityItem ||
      item instanceof SchedulingLinkBaselineAvailabilityItem;
    ExistingEventGestures.registerSpanGestures(manager, host, spanGuard, {
      resize: true,
    });
    manager.addGesture(
      new ExistingEventGestures.ContextMenu({ guard: menuGuard, host }),
    );
  }

  isSchedulingLinkRow(rowId: RowLocalId): boolean {
    return this.#schedulingLinkRowIds.has(rowId);
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  makeCell(): HTMLElement {
    return document.createElement("div");
  }

  bindCell(_$cell: HTMLElement, _context: BindCellContext): void {}

  onBindRow(): void {}
  onResize(): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}

  #calendarProvider: CalendarSubcolumn.Provider = ((
    self,
  ) => ({
    subcolumnFromRow(
      sheet: SheetViewModel.Surface<SheetBinder>,
      row: SheetRow.Joined.Handle,
      _parentRow: SheetRow.Joined.Handle,
    ): CalendarSubcolumn {
      const rowId = row.id;
      self.#schedulingLinkRowIds.add(rowId);

      const attrs = row.attributesAs(SchedulingLinkAttributes.decode);
      if (attrs?.renderMode === "spans") {
        self.#delegate?.setContext?.(row);
        return makeBaselineSpanSubcolumn(
          sheet,
          row,
          self.#delegate,
          schedulingLinkAvailabilityRowKey(row),
        );
      }

      // Set context on the delegate so persistence uses this row's cell writer
      self.#delegate?.setContext?.(row);
      const availabilityRowKey = schedulingLinkAvailabilityRowKey(row);

      return {
        rowId: RowId.fromLocalId(rowId),
        width: { type: "natural" },
        loadData: async (date) => {
          const [pd, tz] = date;
          const start = tz.toWallClock(pd.start.withTime()).toUtc();
          const end = tz.toWallClock(pd.end.withTime()).toUtc();
          const range = new DateTime.Range(start, end);
          if (availabilityRowKey) {
            await fetchAvailability(sheet, availabilityRowKey, range);
            return;
          }
          const rh = row.joined.rowHandle;
          if (!rh) return;
          await rh.fetch(range);
        },
        ...(self.#delegate ? { createHandler: {
          onDragCreate: (timeRange, context) => self.#installedDelegate.onDragCreate(timeRange, context),
          onClickCreate: (position, context) => self.#installedDelegate.onClickCreate(position, context),
          updateGhost: (timeRange) => self.#installedDelegate.showNewGhost(timeRange),
          clearGhost: () => self.#installedDelegate.clearNewGhost(),
          isCreateRevealed: () => self.#installedDelegate.isCreateRevealed(),
          dismissCreate: () => self.#installedDelegate.dismissCreate(),
        } } : {}),
        makeLayout: () => CalendarColumnLayoutGroup.make(),
        makeSpans: async ({ windowed }) => {
          const range = new DateTime.Range(
            windowed.fragment.start.toUtc(),
            windowed.fragment.end.toUtc(),
          );
          const cells = availabilityRowKey
            ? sheet.cells.peekInRange(RowId.of(availabilityRowKey), range)
            : row.joined.rowHandle?.getAllTimepointsInRange(range) ?? [];
          const spans: CalendarSubcolumnSpan[] = [];
          for (const cell of cells) {
            const override = availabilityOverrideFromValue(cell.value);
            if (!override) continue;
            const slot: AvailabilitySlot = {
              id: `avail-${rowId}-${cell.id}`,
              cellId: cell.id,
              timeRange: cell.dtr,
              override,
            };
            const isClosed = slot.override === "closed";
            spans.push({
              id: slot.id,
              start: slot.timeRange.start,
              end: slot.timeRange.end,
              pointerEvents: "all",
              item: self.#delegate
                ? new SchedulingLinkAvailabilityItem(slot, self.#delegate)
                : null,
              callbacks: null,
              bindContent: (existing) => {
                const $content = existing instanceof CalendarColumnCellContent
                  ? existing
                  : CalendarColumnCellContent.make();
                $content.style.display = "none";
                return $content;
              },
              applyToCell: ($cell) => {
                $cell.style.setProperty(
                  "--std-calendar-cell-background",
                  isClosed ? "var(--red)" : "var(--green)",
                );
                $cell.style.opacity = isClosed ? "0.26" : "0.2";
                $cell.style.borderRadius = "4px";
                $cell.style.border = isClosed
                  ? "1px solid color-mix(in srgb, var(--red) 70%, transparent)"
                  : "1px solid color-mix(in srgb, var(--green) 60%, transparent)";
              },
            });
          }
          return spans;
        },
      };
    },
  }))(this);

  contentProviders: ContentProviders = {
    calendar: this.#calendarProvider,
  };
}

function availabilityOverrideFromValue(
  value: Option<Json | CellValue>,
): Option<AvailabilityOverride> {
  const record = Json.object(Json.wire(value));
  if (record == null) return null;
  const kind = Json.string(record, "kind");
  const override = Json.string(record, "override");
  if (
    kind !== "availability_override" &&
    override !== "open" &&
    override !== "closed" &&
    !(record["start"] && record["end"])
  ) {
    return null;
  }
  return override === "closed" ? "closed" : "open";
}

function schedulingLinkAvailabilityRowKey(
  row: SheetRow.Joined.Handle,
): Option<string> {
  const key = row.lookupKey;
  if (key?.startsWith(":bookings:")) return key;
  const attrs = row.attributesAs(SchedulingLinkAttributes.decode);
  if (attrs?.slug) return `:bookings:${attrs.slug}`;
  return null;
}

/**
 * Fetch the availability row's cells over `range` through the sheet's cell
 * store, keyed by the row's lookup key — the row need not be a row of the
 * sheet: the store mints its handle on first read (`platformOf(handle)
 * .cellsIn`), then makes it fresh for the range's days.
 */
async function fetchAvailability(
  sheet: SheetViewModel.Surface<SheetBinder>,
  availabilityRowKey: string,
  range: DateTime.Range<Utc>,
): Promise<void> {
  const days = new NaiveDate.Range(range.start.date, range.end.date);
  const platform = platformOf(sheet.handle);
  const availabilityRow = RowId.of(availabilityRowKey);
  platform.cellsIn(availabilityRow, days);
  await platform.ensureFresh(availabilityRow, days);
}

function makeBaselineSpanSubcolumn(
  sheet: SheetViewModel.Surface<SheetBinder>,
  row: SheetRow.Joined.Handle,
  delegate: Option<SchedulingLinkItemDelegate>,
  availabilityRowKey: Option<string>,
): CalendarSubcolumn {
  const rowId = row.id;
  return {
    rowId: RowId.fromLocalId(rowId),
    stagger: false,
    width: { type: "preferred", width: "24px" },
    makeLayout: () => {
      const $c = SpanColumn.make();
      $c.bind([24]);
      return $c;
    },
    loadData: async (date) => {
      if (!availabilityRowKey) return;
      const [pd, tz] = date;
      const start = tz.toWallClock(pd.start.withTime()).toUtc();
      const end = tz.toWallClock(pd.end.withTime()).toUtc();
      await fetchAvailability(
        sheet,
        availabilityRowKey,
        new DateTime.Range(start, end),
      );
    },
    makeSpans: async ({ windowed, tz: tzr }) => {
      const attrs = row.attributesAs(SchedulingLinkAttributes.decode);
      const blocks = (attrs?.availability ?? []).filter((b) => b.isAvailable);
      const date = windowed.fragment.date;
      const dow = date.dayOfWeek.dow;
      const forDay = blocks.filter((b) => b.dayOfWeek === dow);
      const range = new DateTime.Range(
        windowed.fragment.start.toUtc(),
        windowed.fragment.end.toUtc(),
      );
      const overrideCells = availabilityRowKey
        ? sheet.cells.peekInRange(RowId.of(availabilityRowKey), range)
        : [];
      const closedOverrides = overrideCells
        .filter((cell) => availabilityOverrideFromValue(cell.value) === "closed")
        .map((cell) => cell.dtr);

      const baselineSpans = forDay.flatMap((b) => {
        const startTod = new TimeOfDay(b.startMinutes * Time.MS_PER_MIN);
        const endTod = new TimeOfDay(b.endMinutes * Time.MS_PER_MIN);
        const start = tzr.toWallClock(date.withTime(startTod)).toUtc();
        const end = tzr.toWallClock(date.withTime(endTod)).toUtc();
        return subtractRanges(
          new DateTime.Range(start, end),
          closedOverrides,
        ).map((segment) =>
          makeBaselineSpan(
            `avail-baseline-${rowId}-${b.dayOfWeek}-${b.startMinutes}-${segment.start.toString()}`,
            segment,
            delegate,
            row,
          )
        );
      });

      return [
        ...baselineSpans,
        ...overrideCells.flatMap((cell) => {
          const span = makeOverrideSpan(rowId, cell, delegate);
          return span ? [span] : [];
        }),
      ];
    },
  };
}

function makeBaselineSpan(
  id: string,
  time: DateTime.Range<Utc>,
  delegate: Option<SchedulingLinkItemDelegate>,
  row: SheetRow.Joined.Handle,
): CalendarSubcolumnSpan {
  const $e = SpanFlat.make();
  $e.style.background = "var(--green-dimmed)";
  $e.style.opacity = "0.15";
  $e.style.setProperty("--icon-bg", "transparent");
  const slot: AvailabilityBaselineSlot = {
    id,
    timeRange: time,
    override: "open",
  };
  return {
    id,
    bindContent: ContentSpan.staticContent($e),
    start: time.start,
    end: time.end,
    pointerEvents: delegate ? "all" : "none",
    item: delegate
      ? new SchedulingLinkBaselineAvailabilityItem(slot, delegate, row)
      : null,
    callbacks: null,
  };
}

function makeOverrideSpan(
  rowId: RowLocalId,
  cell: TimepointCellEntry,
  delegate: Option<SchedulingLinkItemDelegate>,
): Option<CalendarSubcolumnSpan> {
  const override = availabilityOverrideFromValue(cell.value);
  if (!override) return null;
  const slot: AvailabilitySlot = {
    id: `avail-${rowId}-${cell.id}`,
    cellId: cell.id,
    timeRange: cell.dtr,
    override,
  };
  const $e = SpanFlat.make();
  $e.style.background = override === "closed" ? "var(--red)" : "var(--green)";
  $e.style.opacity = override === "closed" ? "0.34" : "0.22";
  $e.style.setProperty("--icon-bg", "transparent");
  $e.style.borderRadius = "4px";
  $e.style.border = override === "closed"
    ? "1px solid color-mix(in srgb, var(--red) 70%, transparent)"
    : "1px solid color-mix(in srgb, var(--green) 60%, transparent)";
  return {
    id: slot.id,
    bindContent: ContentSpan.staticContent($e),
    start: slot.timeRange.start,
    end: slot.timeRange.end,
    pointerEvents: delegate ? "all" : "none",
    item: delegate
      ? new SchedulingLinkAvailabilityItem(slot, delegate)
      : null,
    callbacks: null,
  };
}

function subtractRanges(
  base: DateTime.Range<Utc>,
  removals: readonly DateTime.Range<Utc>[],
): DateTime.Range<Utc>[] {
  let segments = [base];
  const sorted = [...removals].sort((a, b) => a.start.mse - b.start.mse);
  for (const removal of sorted) {
    segments = segments.flatMap((segment) => subtractOne(segment, removal));
  }
  return segments;
}

function subtractOne(
  base: DateTime.Range<Utc>,
  removal: DateTime.Range<Utc>,
): DateTime.Range<Utc>[] {
  if (removal.end.mse <= base.start.mse || removal.start.mse >= base.end.mse) {
    return [base];
  }
  const segments: DateTime.Range<Utc>[] = [];
  const leftEnd = removal.start.mse < base.end.mse ? removal.start : base.end;
  if (leftEnd.mse > base.start.mse) {
    segments.push(new DateTime.Range(base.start, leftEnd));
  }
  const rightStart = removal.end.mse > base.start.mse ? removal.end : base.start;
  if (rightStart.mse < base.end.mse) {
    segments.push(new DateTime.Range(rightStart, base.end));
  }
  return segments;
}
